import React, { FC, useState } from 'react';
import { AiOutlineSearch } from 'react-icons/ai';
import { APP_BG, APP_BORDER, APP_TEXT } from '@/constant/theme/colors.constant';
import { tid } from '@/utils/testId';

interface GlobalSearchProps {
  globalSearch?: string;
  setGlobalSearch: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  isDataFound?: boolean;
  dataTestId?: string;
}

const GlobalSearch: FC<GlobalSearchProps> = ({
  globalSearch = '',
  placeholder = 'Search...',
  setGlobalSearch,
  className,
  disabled = false,
  isDataFound = true,
  dataTestId = '',
}) => {
  const [lastKeyPressed, setLastKeyPressed] = useState('');
  const [isFocused, setIsFocused] = useState(false); // Track focus state

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isDataFound) {
      setGlobalSearch(e.target.value);
    } else if (!isDataFound && lastKeyPressed === 'Backspace') {
      setGlobalSearch(e.target.value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    setLastKeyPressed(e.key);
    if (!isDataFound && e.key !== 'Backspace') {
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteText = e.clipboardData.getData('text');
    const trimmedText = pasteText.trimEnd();
    setGlobalSearch(trimmedText);
  };

  return (
    <span
      className={`relative flex min-w-0 items-center ${className ?? 'w-full'}`}
    >
      <AiOutlineSearch
        className={`pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors duration-150 ${isFocused ? APP_TEXT.primary : APP_TEXT.subtle
          }`}
      />
      <input
        tabIndex={0}
        value={globalSearch}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        readOnly={disabled}
        className={`h-10 pl-8 pr-3 rounded-lg border text-sm font-normal ${APP_BG.input}
        ${APP_TEXT.default} placeholder-gray-400 dark:placeholder-gray-500
        transition-colors duration-150 focus:outline-none w-full
        ${isFocused
            ? `border-primary ring-1 ring-primary/20`
            : APP_BORDER.default
          }`}
        type="text"
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        data-testid={dataTestId || tid('globalSearch-input')}
      />
    </span>
  );
};

export default GlobalSearch;
